import {
    Component,
    ElementRef,
    ViewChild,
    Input,
    OnInit,
    Output,
    EventEmitter,
    ViewEncapsulation,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { ThreeSixtyFactory } from './three-sixty.factory';
import { HotspotInterface } from '@mediaman/three-sixty/dist/interfaces/hotspot.interface';
import { ConfigurationInterface } from '@mediaman/three-sixty/dist/interfaces/configuration.interface';
import { ImageSetInterface } from '@mediaman/three-sixty/dist/interfaces/image-set.interface';
import ThreeSixty from '@mediaman/three-sixty';

@Component({
    selector: 'mm-three-sixty',
    styles: ['.three-sixty-container{position:relative;cursor:move;cursor:-webkit-grab;-ms-touch-action:none;touch-action:none}.three-sixty__hotspot{display:none;position:absolute;width:200px;padding:10px 15px;background:rgba(0,0,0,.5);border-radius:20px;color:#fff}.three-sixty__hotspot--active{display:block}'],
    template: `<canvas #canvasElement class="mm-three-sixty" [width]="width" [height]="height"></canvas>`,
    encapsulation: ViewEncapsulation.None
})
export class ThreeSixtyComponent implements OnInit, OnChanges {

    /**
     * The canvas width
     */
    @Input() public width: number;

    /**
     * The canvas height
     */
    @Input() public height: number;

    /**
     * Total amount of angles
     */
    @Input() public angles: number;

    /**
     * Amount of angles per image
     */
    @Input() public anglesPerImage: number;

    /**
     * The initial angle to show (number between 0 and 360)
     */
    @Input() public startAngle: number = 0;

    /**
     * Image set with all (ordered) image urls
     */
    @Input() public images: ImageSetInterface = {};

    /**
     * The factor which increases the drag speed
     * Default to 5
     */
    @Input() public speedFactor: number;

    /**
     * Array of hotspots
     */
    @Input() public hotspots: HotspotInterface[];

    /**
     * Flag to control if all images should be preloaded
     */
    @Input() public preload: boolean = false;

    /**
     * Gets emitted when the images were preloaded
     */
    @Output() public preloaded: EventEmitter<null> = new EventEmitter();

    /**
     * The canvas element reference
     */
    @ViewChild('canvasElement') private canvasElement: ElementRef;

    /**
     * The three sixty instance
     */
    private threeSixty: ThreeSixty;

    /**
     * @param threeSixtyFactory
     */
    public constructor(private threeSixtyFactory: ThreeSixtyFactory) {
    }

    /**
     * @inheritDoc
     */
    public ngOnInit() {
        this.threeSixty = this.threeSixtyFactory.create(this.canvasElement.nativeElement, this.getThreeSixtyConfiguration());

        this.threeSixty.initialize(this.images, this.startAngle);

        this.preloadImages();
    }

    /**
     * @inheritDoc
     */
    public ngOnChanges(changes: SimpleChanges) {
        if (!this.threeSixty) {
            return;
        }

        // Don't update the configuration if only the images have been changed
        const changedProperties = Object.keys(changes);
        if (changedProperties.length > 1 || !changes.hasOwnProperty('images')) {
            this.threeSixty.updateConfiguration(this.getThreeSixtyConfiguration());
        }

        if (changes.hasOwnProperty('images')) {
            this.threeSixty.updateImages(this.images);
            this.preloadImages();
        }
    }

    /**
     * Force a preload of all images
     */
    public forcePreload() {
        this.threeSixty.preload().then(() => this.preloaded.emit());
    }

    /**
     * Preload all images if the preload flag is set
     */
    private preloadImages() {
        if (this.preload) {
            this.threeSixty.preload().then(() => this.preloaded.emit());
        }
    }

    /**
     * Get the configuration object for the ThreeSixty instance
     */
    private getThreeSixtyConfiguration(): ConfigurationInterface {
        const configuration: ConfigurationInterface = {
            angles: this.angles,
            anglesPerImage: this.anglesPerImage
        };

        if (this.speedFactor >= 0) {
            configuration.speedFactor = this.speedFactor;
        }

        if (this.hotspots) {
            configuration.hotspots = this.hotspots;
        }
        return configuration;
    }
}
